import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import {
  EMPTY_PLAYYARD_SKY_PREVIEW,
  petYardWeatherConditionToLabel,
  playyardSkyPhaseToLabel,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  PetYardWeatherCondition,
  PlayyardSkyPhase,
  PlayyardSkyPreview,
} from "@/types";

// Admin-only: pin the yard to any time of day and weather to check how it
// looks without waiting for Nashville to cooperate.
export const PlayyardSkyPreviewPicker = ({
  preview,
  setPreview,
}: {
  preview: PlayyardSkyPreview;
  setPreview: (preview: PlayyardSkyPreview) => void;
}) => {
  return (
    <Column className="gap-3">
      <Row className="items-center justify-between border-b-2 border-dashed border-slate-300 pb-2">
        <Text className="font-mono text-[11px] font-black uppercase tracking-[0.1em] text-slate-900">
          Sky preview
        </Text>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setPreview(EMPTY_PLAYYARD_SKY_PREVIEW);
          }}
          className="h-6 rounded-none px-2 font-mono text-[9px] font-black uppercase tracking-[0.1em] text-violet-700 hover:bg-violet-100"
        >
          Back to live
        </Button>
      </Row>
      <PreviewGroup label="Time of day">
        <PreviewOption
          label="Live"
          isSelected={preview.phase === null}
          onSelect={() => {
            setPreview({ ...preview, phase: null });
          }}
        />
        {Object.values(PlayyardSkyPhase).map((phase) => (
          <PreviewOption
            key={phase}
            label={playyardSkyPhaseToLabel[phase]}
            isSelected={preview.phase === phase}
            onSelect={() => {
              setPreview({ ...preview, phase });
            }}
          />
        ))}
      </PreviewGroup>
      <PreviewGroup label="Weather">
        <PreviewOption
          label="Live"
          isSelected={preview.condition === null}
          onSelect={() => {
            setPreview({ ...preview, condition: null });
          }}
        />
        {Object.values(PetYardWeatherCondition).map((condition) => (
          <PreviewOption
            key={condition}
            label={petYardWeatherConditionToLabel[condition]}
            isSelected={preview.condition === condition}
            onSelect={() => {
              setPreview({ ...preview, condition });
            }}
          />
        ))}
      </PreviewGroup>
      <PreviewGroup label="Extras">
        <PreviewOption
          label="Windy"
          isSelected={preview.isWindy}
          onSelect={() => {
            setPreview({ ...preview, isWindy: !preview.isWindy });
          }}
        />
        <PreviewOption
          label="Freezing"
          isSelected={preview.isFreezing}
          onSelect={() => {
            setPreview({ ...preview, isFreezing: !preview.isFreezing });
          }}
        />
      </PreviewGroup>
    </Column>
  );
};

const PreviewGroup = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <Column className="gap-1.5">
      <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
        {label}
      </Text>
      <Row className="flex-wrap gap-1.5">{children}</Row>
    </Column>
  );
};

const PreviewOption = ({
  label,
  isSelected,
  onSelect,
}: {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <Button
      type="button"
      variant="outlineDark"
      onClick={onSelect}
      className={cn(
        "h-7 rounded-none border-2 border-slate-900 bg-white px-2 font-mono text-[10px] font-black uppercase tracking-[0.06em] text-slate-900 shadow-[2px_2px_0_#172033] hover:bg-amber-100",
        isSelected && "bg-slate-900 text-orange-50 hover:bg-slate-800",
      )}
    >
      {label}
    </Button>
  );
};
